import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';

const SitemapUrlModal = ({ site, isOpen, onClose, onUrlSelect, onMultiUrlSelect, addedUrls = [], existingUrls = [], multiSelect = false, viewOnly = false, loadingExistingPages = false }) => {
  const { currentLang, token, API_URL, getCsrfToken } = useAuth();
  const [sitemapUrls, setSitemapUrls] = useState([]);
  const [sitemapData, setSitemapData] = useState([]);
  const [totalUrls, setTotalUrls] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUrls, setSelectedUrls] = useState([]);

  useEffect(() => {
    if (isOpen && site?.sitemaps) {
      fetchSitemapUrls();
      setSelectedUrls([]);
    }
  }, [isOpen, site]);

  const fetchSitemapUrls = async () => {
    try {
      setLoading(true);
      setError(null);

      // Проверяем наличие site_id
      if (!site?.id) {
        setError(currentLang === 'ru' ? 'ID сайта не указан' : 'Site ID is not specified');
        return;
      }

      // Делаем запрос через наш бэкенд
      try {
        const currentCsrfToken = await getCsrfToken();
        if (!currentCsrfToken) {
          throw new Error('Не удалось получить CSRF токен');
        }

        const response = await fetch(`${API_URL}${currentLang}/seo/sitemap/parse`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CSRF-Token': currentCsrfToken,
          },
          credentials: 'include',
          body: JSON.stringify({ site_id: site.id }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            // Сохраняем полные данные sitemap
            setSitemapData(data.urls || []);
            
            // Извлекаем URL из объектов sitemap
            const validUrls = Array.isArray(data.urls) 
              ? data.urls
                  .filter(item => item && typeof item.loc === 'string')
                  .map(item => item.loc)
              : [];
            setSitemapUrls(validUrls);
            setTotalUrls(data.urls_count || validUrls.length);
            return;
          }
        }
      } catch (backendError) {
        setError(currentLang === 'ru' 
          ? 'Ошибка загрузки sitemap. Попробуйте позже.' 
          : 'Error loading sitemap. Please try again later.');
      }
    } catch (error) {
      
      // Более детальные сообщения об ошибках
      if (error.message.includes('CORS') || error.message.includes('blocked')) {
        setError(currentLang === 'ru' 
          ? 'Ошибка CORS при загрузке sitemap. Запрос заблокирован браузером.' 
          : 'CORS error loading sitemap. Request blocked by browser.');
      } else if (error.message.includes('404')) {
        setError(currentLang === 'ru' 
          ? 'Sitemap не найден. Проверьте настройки сайта.' 
          : 'Sitemap not found. Please check site settings.');
      } else if (error.message.includes('timeout')) {
        setError(currentLang === 'ru' 
          ? 'Превышено время ожидания при загрузке sitemap. Попробуйте позже.' 
          : 'Timeout loading sitemap. Please try again later.');
      } else {
        setError(currentLang === 'ru' 
          ? `Ошибка загрузки sitemap: ${error.message}` 
          : `Error loading sitemap: ${error.message}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleUrlSelect = (url) => {
    if (multiSelect) {
      // В режиме мульти-выбора добавляем/убираем из выбранных
      setSelectedUrls(prev => 
        prev.includes(url) 
          ? prev.filter(u => u !== url)
          : [...prev, url]
      );
    } else {
      // В обычном режиме сразу выбираем URL
      onUrlSelect(url);
      onClose();
    }
  };

  const handleMultiSelect = () => {
    if (selectedUrls.length > 0 && onMultiUrlSelect) {
      onMultiUrlSelect(selectedUrls);
      setSelectedUrls([]);
      onClose();
    }
  };

  const handleSelectAll = () => {
    const availableUrls = filteredSitemapData
      .map(item => item.loc)
      .filter(url => !addedUrls.includes(url) && !existingUrls.includes(url));
    setSelectedUrls(availableUrls);
  };

  const handleDeselectAll = () => {
    setSelectedUrls([]);
  };

  const filteredUrls = sitemapUrls.filter(url => 
    typeof url === 'string' && url.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredSitemapData = sitemapData.filter(item => 
    item && typeof item.loc === 'string' && item.loc.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Функция для подсчета общего количества найденных URL
  const getTotalFoundUrls = () => {
    return filteredSitemapData.length;
  };

  const totalFoundUrls = getTotalFoundUrls();

  const handleClose = () => {
    setSearchTerm('');
    setError(null);
    setTotalUrls(0);
    setSitemapData([]);
    setSelectedUrls([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-2xl mx-auto max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-center mb-4 sm:mb-6">
            <h3 className="text-lg sm:text-xl font-bold text-gray-800">
              {viewOnly 
                ? (currentLang === 'ru' ? 'Sitemap сайта' : 'Site Sitemap')
                : (currentLang === 'ru' ? 'Выбор URL из Sitemap' : 'Select URL from Sitemap')
              }
            </h3>
            <button
              onClick={handleClose}
              className="text-gray-400 hover:text-gray-600 p-1 transition-colors"
            >
              <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              <div className="whitespace-pre-line mb-3">{error}</div>
              <button
                onClick={fetchSitemapUrls}
                disabled={loading}
                className="px-3 py-1.5 bg-red-600 text-white rounded text-xs hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  currentLang === 'ru' ? 'Загрузка...' : 'Loading...'
                ) : (
                  currentLang === 'ru' ? 'Попробовать снова' : 'Try again'
                )}
              </button>
            </div>
          )}

          {/* Информация о sitemap */}
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2 text-sm text-blue-700">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="font-medium">
                {currentLang === 'ru' ? 'Sitemap:' : 'Sitemap:'}
              </span>
              <a 
                href={site?.sitemaps} 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline break-all"
              >
                {site?.sitemaps}
              </a>
            </div>
          </div>

          {/* Поиск */}
          <div className="mb-4">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={currentLang === 'ru' ? 'Поиск по URL...' : 'Search URLs...'}
              className="w-full p-2.5 sm:p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm transition-colors"
            />
          </div>

          {/* Кнопки управления для мульти-выбора */}
          {multiSelect && !viewOnly && (
            <div className="mb-4 flex flex-wrap gap-2">
              <button
                onClick={handleSelectAll}
                className="px-3 py-1.5 bg-blue-500 text-white text-xs rounded-lg hover:bg-blue-600 transition-colors"
                disabled={loadingExistingPages}
              >
                {currentLang === 'ru' ? 'Выбрать все доступные' : 'Select all available'}
              </button>
              <button
                onClick={handleDeselectAll}
                className="px-3 py-1.5 bg-gray-500 text-white text-xs rounded-lg hover:bg-gray-600 transition-colors"
              >
                {currentLang === 'ru' ? 'Снять выбор' : 'Deselect all'}
              </button>
              {selectedUrls.length > 0 && (
                <span className="px-3 py-1.5 bg-green-100 text-green-700 text-xs rounded-lg">
                  {currentLang === 'ru' ? `Выбрано: ${selectedUrls.length}` : `Selected: ${selectedUrls.length}`}
                </span>
              )}
              {loadingExistingPages && (
                <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 text-xs rounded-lg flex items-center gap-1">
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-yellow-600"></div>
                  {currentLang === 'ru' ? 'Проверка существующих...' : 'Checking existing...'}
                </span>
              )}
            </div>
          )}

          {/* Список URL */}
          <div className="max-h-96 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                <span className="ml-3 text-gray-600">
                  {currentLang === 'ru' ? 'Загрузка sitemap...' : 'Loading sitemap...'}
                </span>
              </div>
            ) : filteredSitemapData.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {searchTerm ? (
                  currentLang === 'ru' ? 'URL не найдены' : 'No URLs found'
                ) : (
                  currentLang === 'ru' ? 'Sitemap пуст или недоступен' : 'Sitemap is empty or unavailable'
                )}
              </div>
            ) : (
              <div className="space-y-2">
                {filteredSitemapData.map((item, index) => {
                  const isAdded = addedUrls.includes(item.loc);
                  const isExisting = existingUrls.includes(item.loc);
                  const isSelected = selectedUrls.includes(item.loc);
                  
                  return (
                    <div
                      key={index}
                      onClick={viewOnly || isAdded || isExisting ? undefined : () => handleUrlSelect(item.loc)}
                      className={`p-3 border rounded-lg transition-colors ${
                        isExisting 
                          ? 'bg-red-50 border-red-200' 
                          : isAdded 
                            ? 'bg-green-50 border-green-200' 
                            : viewOnly 
                              ? 'bg-gray-50 border-gray-200' 
                              : isSelected
                                ? 'bg-blue-50 border-blue-300 hover:bg-blue-100'
                                : 'border-gray-200 hover:bg-blue-50 hover:border-blue-300 cursor-pointer'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            {/* Чекбокс для мульти-выбора */}
                            {multiSelect && !viewOnly && !isAdded && !isExisting && (
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleUrlSelect(item.loc);
                                }}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                              />
                            )}
                            
                            {/* Иконка статуса */}
                            {isExisting && (
                              <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                              </svg>
                            )}
                            {isAdded && !isExisting && (
                              <svg className="w-4 h-4 text-green-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                              </svg>
                            )}
                            
                            <div className="text-sm text-gray-800 break-all">{item.loc}</div>
                          </div>
                          
                          <div className="flex flex-wrap items-center gap-2 mt-2 text-xs text-gray-500">
                            {item.lastmod && (
                              <span className="bg-gray-100 px-2 py-1 rounded">
                                {currentLang === 'ru' ? 'Обновлено:' : 'Updated:'} {new Date(item.lastmod).toLocaleDateString()}
                              </span>
                            )}
                            {item.changefreq && (
                              <span className="bg-blue-100 px-2 py-1 rounded">
                                {currentLang === 'ru' ? 'Частота:' : 'Freq:'} {item.changefreq}
                              </span>
                            )}
                            {item.priority !== null && item.priority !== undefined && (
                              <span className="bg-green-100 px-2 py-1 rounded">
                                {currentLang === 'ru' ? 'Приоритет:' : 'Priority:'} {item.priority}
                              </span>
                            )}
                          </div>
                          
                          <div className="text-xs text-gray-500 mt-1">
                            {isExisting 
                              ? (currentLang === 'ru' ? 'Уже существует на сайте' : 'Already exists on site')
                              : isAdded 
                                ? (currentLang === 'ru' ? 'Уже добавлен в форму' : 'Already added to form')
                                : viewOnly 
                                  ? (currentLang === 'ru' ? 'URL из sitemap' : 'URL from sitemap')
                                  : multiSelect
                                    ? (currentLang === 'ru' ? 'Нажмите для выбора' : 'Click to select')
                                    : (currentLang === 'ru' ? 'Нажмите для выбора' : 'Click to select')
                            }
                          </div>
                        </div>
                        
                        {!viewOnly && !isAdded && !isExisting && (
                          <svg className="w-4 h-4 text-gray-400 ml-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                          </svg>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Статистика */}
          {!loading && filteredSitemapData.length > 0 && (
            <div className="mt-4 p-3 bg-gray-50 rounded-lg">
              <div className="text-sm text-gray-600">
                {currentLang === 'ru' ? (
                  <>
                    Показано: <span className="font-medium">{filteredSitemapData.length}</span> из{' '}
                    <span className="font-medium">{totalFoundUrls}</span> URL
                    {existingUrls.length > 0 && (
                      <span className="ml-2 text-red-600">
                        • Существует на сайте: <span className="font-medium">{existingUrls.length}</span>
                      </span>
                    )}
                    {addedUrls.length > 0 && (
                      <span className="ml-2 text-green-600">
                        • Добавлено в форму: <span className="font-medium">{addedUrls.length}</span>
                      </span>
                    )}
                  </>
                ) : (
                  <>
                    Showing: <span className="font-medium">{filteredSitemapData.length}</span> of{' '}
                    <span className="font-medium">{totalFoundUrls}</span> URLs
                    {existingUrls.length > 0 && (
                      <span className="ml-2 text-red-600">
                        • Exists on site: <span className="font-medium">{existingUrls.length}</span>
                      </span>
                    )}
                    {addedUrls.length > 0 && (
                      <span className="ml-2 text-green-600">
                        • Added to form: <span className="font-medium">{addedUrls.length}</span>
                      </span>
                    )}
                  </>
                )}
              </div>
            </div>
          )}

          {/* Кнопки */}
          <div className="flex justify-end gap-2 mt-6">
            {multiSelect && selectedUrls.length > 0 && !viewOnly && (
              <button
                onClick={handleMultiSelect}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-sm"
              >
                {currentLang === 'ru' ? `Добавить выбранные (${selectedUrls.length})` : `Add selected (${selectedUrls.length})`}
              </button>
            )}
            <button
              onClick={handleClose}
              className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-sm"
            >
              {currentLang === 'ru' ? 'Закрыть' : 'Close'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SitemapUrlModal; 